/*
 Centralized audio engine providing:
 - Shared AudioContext lifecycle
 - Mix buses (sfx, voice, ambience) and master processing
 - Lightweight metering via AudioWorklet (optional, auto-loaded)
 - Asset decoding, resampling to context sampleRate, and caching
 - Simple API for one-shots and looped sources
*/

export type AudioBusName = 'sfx' | 'voice' | 'ambience' | 'breathing';

type DecodedBufferCacheKey = string; // `${url}|${sampleRate}`

export interface OneShotOptions {
  startTime?: number; // seconds relative to context.currentTime
  gain?: number; // linear
  playbackRate?: number;
  bus?: AudioBusName;
}

export interface LoopOptions extends OneShotOptions {
  loopStart?: number;
  loopEnd?: number;
}

export class AudioEngine {
  static #instance: AudioEngine | null = null;

  readonly context: AudioContext;
  readonly destination: AudioNode;

  // Buses
  readonly masterGain: GainNode;
  readonly masterDynamics: DynamicsCompressorNode; // pragmatic limiter substitute
  readonly softClipper: WaveShaperNode;
  readonly sfxGain: GainNode;
  readonly sfxComp: DynamicsCompressorNode;
  readonly sfxHPF: BiquadFilterNode;
  readonly voiceGain: GainNode;
  readonly voiceComp: DynamicsCompressorNode;
  readonly voiceHPF: BiquadFilterNode;
  readonly ambienceGain: GainNode;
  readonly ambienceComp: DynamicsCompressorNode;
  readonly ambienceHPF: BiquadFilterNode;
  readonly breathingGain: GainNode;
  readonly breathingComp: DynamicsCompressorNode;
  readonly breathingLPF: BiquadFilterNode;

  // Optional: meter node (worklet-backed)
  #meterNode: AudioWorkletNode | null = null;

  // Asset cache
  #decodedCache: Map<DecodedBufferCacheKey, AudioBuffer> = new Map();

  // Memory/Node pools
  #float32Pools: Map<number, Float32Array[]> = new Map(); // bucketed by length
  #gainPool: GainNode[] = [];
  #pannerPool: StereoPannerNode[] = [];
  #maxArraysPerBucket = 16;
  #maxNodePoolSize = 32;

  private constructor() {
    this.context = new (window.AudioContext || (window as any).webkitAudioContext)({
      latencyHint: 'interactive',
    });

    // Buses and master chain
    this.masterGain = this.context.createGain();
    this.masterGain.gain.value = 0.5; // -6 dB headroom baseline

    this.masterDynamics = this.context.createDynamicsCompressor();
    // Conservative settings to behave like a soft limiter
    this.masterDynamics.threshold.value = -6;
    this.masterDynamics.knee.value = 12;
    this.masterDynamics.ratio.value = 8;
    this.masterDynamics.attack.value = 0.003;
    this.masterDynamics.release.value = 0.050;

    // Per-bus gains + gentle compressors (auto-mix front-end)
    this.sfxGain = this.context.createGain();
    this.sfxGain.gain.value = 0.7; // lower SFX baseline so harmonics can lead
    this.sfxComp = this.#createBusCompressor();
    this.sfxHPF = this.context.createBiquadFilter();
    this.sfxHPF.type = 'highpass';
    this.sfxHPF.frequency.value = 140; // roll off low thumps
    this.sfxHPF.Q.value = 0.707;

    this.voiceGain = this.context.createGain();
    this.voiceGain.gain.value = 1.4; // boost voice/harmonic content
    this.voiceComp = this.#createBusCompressor();
    this.voiceHPF = this.context.createBiquadFilter();
    this.voiceHPF.type = 'highpass';
    this.voiceHPF.frequency.value = 80; // reduce proximity/rumble
    this.voiceHPF.Q.value = 0.707;

    this.ambienceGain = this.context.createGain();
    this.ambienceGain.gain.value = 0.8;
    this.ambienceComp = this.#createBusCompressor();
    this.ambienceHPF = this.context.createBiquadFilter();
    this.ambienceHPF.type = 'highpass';
    this.ambienceHPF.frequency.value = 120; // remove sub-bass from ambience
    this.ambienceHPF.Q.value = 0.707;

    // ── Breathing bus: for respiratory/whoosh sounds ──
    this.breathingGain = this.context.createGain();
    this.breathingGain.gain.value = 1.0;
    this.breathingComp = this.#createBusCompressor();
    this.breathingLPF = this.context.createBiquadFilter();
    this.breathingLPF.type = 'lowpass';
    this.breathingLPF.frequency.value = 800; // breathing is low-frequency dominant
    this.breathingLPF.Q.value = 0.707;

    // Soft clipper to prevent harsh spikes before master dynamics
    this.softClipper = this.context.createWaveShaper();
    (this.softClipper as any).curve = this.#createSoftClipCurve(4096);
    this.softClipper.oversample = '2x';

    // Wire: buses -> bus comps -> masterGain -> softClipper -> dynamics -> destination
    this.sfxGain.connect(this.sfxHPF);
    this.sfxHPF.connect(this.sfxComp);
    this.voiceGain.connect(this.voiceHPF);
    this.voiceHPF.connect(this.voiceComp);
    this.ambienceGain.connect(this.ambienceHPF);
    this.ambienceHPF.connect(this.ambienceComp);
    this.breathingGain.connect(this.breathingLPF);
    this.breathingLPF.connect(this.breathingComp);
    this.sfxComp.connect(this.masterGain);
    this.voiceComp.connect(this.masterGain);
    this.ambienceComp.connect(this.masterGain);
    this.breathingComp.connect(this.masterGain);
    this.masterGain.connect(this.softClipper);
    this.softClipper.connect(this.masterDynamics);

    this.destination = this.masterDynamics;
    this.destination.connect(this.context.destination);

    // Try to load the meter worklet (non-fatal if it fails).
    this.#maybeLoadMeterWorklet();
  }

  static getInstance(): AudioEngine {
    if (!AudioEngine.#instance) {
      AudioEngine.#instance = new AudioEngine();
    }
    return AudioEngine.#instance;
  }

  // -------- Memory pools --------
  acquireFloat32(length: number): Float32Array {
    const bucket = this.#bucketSize(length);
    const pool = this.#float32Pools.get(bucket);
    if (pool && pool.length) {
      return pool.pop()!;
    }
    return new Float32Array(bucket);
  }

  releaseFloat32(buf: Float32Array): void {
    const bucket = buf.length;
    if (!this.#float32Pools.has(bucket)) this.#float32Pools.set(bucket, []);
    const pool = this.#float32Pools.get(bucket)!;
    if (pool.length < this.#maxArraysPerBucket) {
      pool.push(buf);
    }
  }

  #bucketSize(n: number): number {
    // power-of-two bucket up to 1<<24 (~16M)
    let p = 1;
    while (p < n && p < 16777216) p <<= 1;
    return p;
  }

  // -------- Node pools --------
  acquireGain(): GainNode {
    const node = this.#gainPool.pop() || this.context.createGain();
    // Reset default state
    node.gain.cancelScheduledValues(this.context.currentTime);
    node.gain.value = 1.0;
    return node;
  }

  releaseGain(node: GainNode): void {
    try { node.disconnect(); } catch {}
    if (this.#gainPool.length < this.#maxNodePoolSize) this.#gainPool.push(node);
  }

  acquirePanner(): StereoPannerNode {
    const node = this.#pannerPool.pop() || this.context.createStereoPanner();
    node.pan.cancelScheduledValues(this.context.currentTime);
    node.pan.value = 0;
    return node;
  }

  releasePanner(node: StereoPannerNode): void {
    try { node.disconnect(); } catch {}
    if (this.#pannerPool.length < this.#maxNodePoolSize) this.#pannerPool.push(node);
  }

  async resume(): Promise<void> {
    if (this.context.state !== 'running') {
      await this.context.resume();
    }
  }

  getBusGain(bus: AudioBusName): GainNode {
    switch (bus) {
      case 'sfx':       return this.sfxGain;
      case 'voice':     return this.voiceGain;
      case 'ambience':  return this.ambienceGain;
      case 'breathing': return this.breathingGain;
      default:          return this.sfxGain;
    }
  }

  connectToBus(node: AudioNode, bus: AudioBusName = 'sfx'): void {
    node.connect(this.getBusGain(bus));
  }

  async decodeAndCache(url: string): Promise<AudioBuffer> {
    const targetRate = this.context.sampleRate;
    const key = `${url}|${targetRate}`;
    const cached = this.#decodedCache.get(key);
    if (cached) return cached;

    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const decoded = await this.context.decodeAudioData(arrayBuffer.slice(0));

    let buffer = decoded;
    if (decoded.sampleRate !== targetRate) {
      buffer = await this.#resampleBuffer(decoded, targetRate);
    }

    this.#decodedCache.set(key, buffer);
    return buffer;
  }

  async playOneShot(buffer: AudioBuffer, options: OneShotOptions = {}): Promise<{ source: AudioBufferSourceNode; gain: GainNode; }> {
    await this.resume();

    const bus = options.bus || 'sfx';
    const startTime = options.startTime ?? this.context.currentTime + 0.02;
    const gainValue = this.#safeGain(options.gain ?? 1.0);
    const playbackRate = options.playbackRate ?? 1.0;

    const src = this.context.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = playbackRate;
    src.loop = false;

    const gain = this.context.createGain();
    gain.gain.setValueAtTime(0.0001, this.context.currentTime);
    // Click-free short fade in
    gain.gain.linearRampToValueAtTime(gainValue, startTime + 0.01);

    src.connect(gain);
    gain.connect(this.getBusGain(bus));

    // Auto-duck ambience during prominent SFX/voice to reduce low-tone masking
    if (bus === 'sfx' || bus === 'voice') {
      this.duckBus('ambience', 0.5, 0.35, startTime);
    }
    if (bus === 'voice') {
      // Prioritize harmonics: also duck SFX briefly when voice plays
      this.duckBus('sfx', 0.6, 0.3, startTime);
    }

    src.start(startTime);

    // Schedule a small fade out at the end to avoid clicks
    const duration = buffer.duration / Math.max(0.0001, playbackRate);
    const endTime = startTime + duration;
    gain.gain.setValueAtTime(gainValue, Math.max(startTime, endTime - 0.02));
    gain.gain.linearRampToValueAtTime(0.0001, endTime);

    src.onended = () => {
      try { src.disconnect(); } catch {}
      try { gain.disconnect(); } catch {}
    };

    return { source: src, gain };
  }

  async createLoop(buffer: AudioBuffer, options: LoopOptions = {}): Promise<{ source: AudioBufferSourceNode; gain: GainNode; start: () => void; stop: (fadeOut?: number) => void; }>{
    await this.resume();

    const bus = options.bus || 'ambience';
    const gainValue = this.#safeGain(options.gain ?? 1.0);
    const playbackRate = options.playbackRate ?? 1.0;

    const src = this.context.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = playbackRate;
    src.loop = true;
    if (typeof options.loopStart === 'number') src.loopStart = options.loopStart;
    if (typeof options.loopEnd === 'number') src.loopEnd = options.loopEnd!;

    const gain = this.context.createGain();
    gain.gain.setValueAtTime(0.0001, this.context.currentTime);

    src.connect(gain);
    gain.connect(this.getBusGain(bus));

    const start = () => {
      const t = this.context.currentTime + 0.02;
      gain.gain.cancelScheduledValues(this.context.currentTime);
      gain.gain.setValueAtTime(gain.gain.value, this.context.currentTime);
      gain.gain.linearRampToValueAtTime(gainValue, t + 0.02);
      src.start(t);
    };

    const stop = (fadeOut: number = 0.05) => {
      const t = this.context.currentTime;
      const end = t + Math.max(0.01, fadeOut);
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(gain.gain.value, t);
      gain.gain.linearRampToValueAtTime(0.0001, end);
      src.stop(end + 0.001);
    };

    src.onended = () => {
      try { src.disconnect(); } catch {}
      try { gain.disconnect(); } catch {}
    };

    return { source: src, gain, start, stop };
  }

  async #maybeLoadMeterWorklet(): Promise<void> {
    try {
      // Vite-friendly module URL resolution
      // The worklet provides simple channel peak/RMS metering.
      await this.context.audioWorklet.addModule(new URL('./worklets/meter-processor.js', import.meta.url));
      this.#meterNode = new AudioWorkletNode(this.context, 'meter-processor', { numberOfInputs: 1, numberOfOutputs: 1 });
      // Insert meter between masterDynamics and destination for tap
      this.masterDynamics.disconnect();
      this.masterDynamics.connect(this.#meterNode);
      this.#meterNode.connect(this.context.destination);
      // Consumers can subscribe to messages if needed
      // this.#meterNode.port.onmessage = (e) => { /* { rms, peak } */ };
    } catch {
      // Non-fatal; proceed without metering worklet
      this.#meterNode = null;
      try {
        // Ensure we are still connected to destination if previous disconnect happened
        this.masterDynamics.disconnect();
      } catch {}
      try {
        this.masterDynamics.connect(this.context.destination);
      } catch {}
    }
  }

  // Temporarily reduce a target bus gain to improve clarity of transient sounds
  duckBus(target: AudioBusName, level: number = 0.6, duration: number = 0.25, when?: number): void {
    const t0 = Math.max(this.context.currentTime, when ?? this.context.currentTime);
    const busGain = this.getBusGain(target).gain;
    // Read current value safely
    const current = busGain.value;
    const targetValue = Math.max(0, Math.min(current, current * level));
    busGain.cancelScheduledValues(t0);
    busGain.setValueAtTime(current, t0);
    busGain.linearRampToValueAtTime(targetValue, t0 + 0.02);
    // Recover after duration
    const releaseStart = t0 + Math.max(0.02, duration);
    busGain.linearRampToValueAtTime(current, releaseStart + 0.08);
  }

  // Gentle, musical compressor for buses
  #createBusCompressor(): DynamicsCompressorNode {
    const comp = this.context.createDynamicsCompressor();
    comp.threshold.value = -18; // start compressing earlier
    comp.knee.value = 24; // soft knee
    comp.ratio.value = 2.5; // gentle ratio
    comp.attack.value = 0.006;
    comp.release.value = 0.15;
    return comp;
  }

  // Soft clip curve to tame transients without harshness
  #createSoftClipCurve(length: number): Float32Array {
    // Backed explicitly by ArrayBuffer to satisfy stricter lib.dom typings
    const curve = new Float32Array(new ArrayBuffer(length * 4));
    const xMax = 1.0;
    for (let i = 0; i < length; i++) {
      const x = (i / (length - 1)) * 2 - 1; // -1..1
      // Soft clip using tanh-like shaping with pre-gain
      const pre = 1.8; // pre-gain into shaper
      const y = Math.tanh(pre * x);
      curve[i] = y / Math.tanh(pre * xMax);
    }
    return curve;
  }

  // Range-by-range safe gain with smooth transitions to prevent harsh levels
  #safeGain(g: number): number {
    const x = Math.max(0, g);
    // Piecewise mapping with gentle knees between regions
    // 0..0.5: linear
    if (x <= 0.5) return x;
    // 0.5..1.0: compress towards 0.75..0.9
    if (x <= 1.0) {
      const t = (x - 0.5) / 0.5; // 0..1
      const start = 0.5;
      const end = 0.9;
      return start + t * (end - start) * 0.7; // reduce slope
    }
    // 1.0..2.0: strong compression towards 0.95
    if (x <= 2.0) {
      const t = (x - 1.0) / 1.0;
      return 0.9 + t * 0.05; // max 0.95
    }
    // >2.0: clamp just below 1.0
    return 0.97;
  }

  async #resampleBuffer(source: AudioBuffer, targetSampleRate: number): Promise<AudioBuffer> {
    const offline = new OfflineAudioContext({
      numberOfChannels: source.numberOfChannels,
      length: Math.ceil(source.duration * targetSampleRate),
      sampleRate: targetSampleRate,
    });
    const src = offline.createBufferSource();
    src.buffer = source;
    src.connect(offline.destination);
    src.start(0);
    const rendered = await offline.startRendering();
    return rendered;
  }
}

export function getAudioEngine(): AudioEngine {
  return AudioEngine.getInstance();
}


